import { useEffect, useRef } from 'react';
import { PropGetters } from 'downshift';
import styled from '@emotion/styled';
import { useFloating, FloatingPortal } from '@floating-ui/react';
import { ITheme, themed } from '../../themer';
import { DropdownItem, IDropdownItem } from './DropdownItem';
import { DropdownOption } from './DropdownOption';
import { IGetItemDisplayValue, IGetItemKey } from './helpers';
import { Color } from '../../../styles/Color';
import { calculateDropdownMaxHeight } from '../../utils/calculateDropdownMaxHeight';

interface IStyledDropdownListWrapProps {
	isOpen?: boolean;
}

const StyledDropdownListWrap = styled.div<IStyledDropdownListWrapProps, ITheme>`
	position: relative;
	display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
`;

export const StyledDropdownList = styled.div<
	{ floatingElementWidth: number; itemCount: number },
	ITheme
>`
	width: ${({ floatingElementWidth }) =>
		floatingElementWidth ? `${floatingElementWidth}px` : '100%'};
	overflow-y: auto;
	border: 1px solid ${Color.BorderCard};
	border-radius: 8px;
	max-height: ${({ itemCount }) => calculateDropdownMaxHeight({ itemCount })};
	z-index: ${themed(({ dropdownList }) => dropdownList.options.zIndex)};
	background: ${Color.Neutral2};
`;

interface IDropdownListProps {
	getMenuProps: PropGetters<IDropdownItem>['getMenuProps'];
	getItemProps: PropGetters<IDropdownItem>['getItemProps'];
	getItemDisplayValue: IGetItemDisplayValue;
	selectedItem: IDropdownItem | null;
	highlightedIndex: number | null;
	getItemKey: IGetItemKey;
	items: IDropdownItem[];
	isOpen: boolean;
	defaultScrollToNthItem?: number;
	floatingRefs?: ReturnType<typeof useFloating>['refs'];
	strategy?: ReturnType<typeof useFloating>['strategy'];
	x?: number | null;
	y?: number | null;
	floatingElementWidth?: number | null;
	withDropdownFloatingPortal?: boolean;
}

export function DropdownList({
	getItemDisplayValue,
	highlightedIndex,
	selectedItem,
	getMenuProps,
	getItemProps,
	getItemKey,
	isOpen,
	items,
	defaultScrollToNthItem,
	floatingRefs,
	strategy,
	x,
	y,
	floatingElementWidth,
	withDropdownFloatingPortal,
}: IDropdownListProps) {
	const listRef = useRef<HTMLDivElement>(null);
	const optionRef = useRef<HTMLDivElement>(null);

	// defaultScrollToNthItem will automatically scroll to the item by default
	useEffect(() => {
		if (
			defaultScrollToNthItem &&
			listRef.current &&
			optionRef.current &&
			isOpen
		) {
			// eslint-disable-next-line no-param-reassign
			listRef.current.scrollTop = optionRef.current.offsetTop;
		}
	}, [isOpen, defaultScrollToNthItem]);

	const renderDropdown = () => (
		<StyledDropdownList
			{...getMenuProps({ ref: listRef }, { suppressRefError: true })}
			data-testid="list"
			style={{
				position: strategy ?? 'absolute',
				top: y ?? 0,
				left: x ?? 0,
			}}
			floatingElementWidth={floatingElementWidth ?? 0}
			itemCount={items.length}
		>
			{items.map((item: IDropdownItem, index: number) => (
				<DropdownOption
					key={getItemKey(item)}
					ref={index === defaultScrollToNthItem ? optionRef : undefined}
					hasSecondaryStyle={typeof item !== 'string' && item.hasSecondaryStyle}
					isHighlighted={highlightedIndex === index}
					{...getItemProps({
						index,
						item,
						isSelected: selectedItem === item,
					})}
				>
					<DropdownItem
						displayValue={getItemDisplayValue(item)}
						hint={typeof item !== 'string' ? item.hint : undefined}
					/>
				</DropdownOption>
			))}
		</StyledDropdownList>
	);

	return (
		<StyledDropdownListWrap isOpen={isOpen} ref={floatingRefs?.setFloating}>
			{isOpen &&
				(withDropdownFloatingPortal ? (
					<FloatingPortal root={listRef}>{renderDropdown()}</FloatingPortal>
				) : (
					renderDropdown()
				))}
		</StyledDropdownListWrap>
	);
}
